// Renders all active toasts in a fixed bottom-right stack. Subscribes to the
// global toastsSignal so any module that imports showToast() can surface UI
// feedback without prop-drilling a context.

import { X } from "lucide-preact";
import { toastsSignal, dismissToast, type Toast } from "../lib/toast";

const KIND_STYLES: Record<Toast["kind"], string> = {
  success:
    "border-[color:var(--color-secondary)] bg-[color:var(--color-secondary-container)] text-[color:var(--color-on-secondary-container)]",
  error:
    "border-[color:var(--color-error)] bg-[color:var(--color-error-container)] text-[color:var(--color-on-error-container)]",
  info:
    "border-[color:var(--color-tertiary)] bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]",
};

const KIND_ICON: Record<Toast["kind"], string> = {
  success: "✓",
  error: "!",
  info: "i",
};

export function ToastHost(): preact.JSX.Element | null {
  const toasts = toastsSignal.value;
  if (toasts.length === 0) return null;

  return (
    <div
      class="fixed bottom-[calc(56px+env(safe-area-inset-bottom)+0.75rem)] right-4 md:bottom-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Benachrichtigungen"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem(props: { toast: Toast }): preact.JSX.Element {
  const { toast } = props;
  return (
    <div
      role={toast.kind === "error" ? "alert" : "status"}
      aria-live={toast.kind === "error" ? "assertive" : "polite"}
      class={`juuplan-toast pointer-events-auto flex items-start gap-2 rounded border px-3 py-2 text-sm shadow-level-3 min-w-[220px] max-w-[calc(100vw-32px)] sm:max-w-[360px] ${KIND_STYLES[toast.kind]}`}
    >
      <span
        aria-hidden="true"
        class="flex size-5 shrink-0 items-center justify-center rounded-full bg-current/10 text-xs font-bold"
      >
        {KIND_ICON[toast.kind]}
      </span>
      <p class="flex-1 leading-snug whitespace-pre-wrap break-words">{toast.message}</p>
      <button
        type="button"
        onClick={() => dismissToast(toast.id)}
        class="shrink-0 -m-1 rounded p-1.5 opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        aria-label="Schließen"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}
